import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import {
  CalendarDays,
  Check,
  Database,
  FileText,
  Github,
  Link2,
  Loader2,
  Search,
} from 'lucide-react'
import { trpc } from '@/config/trpc'
import type { ConnectorProvider } from './item-defaults'

// Picks a connector target from the brand's connection. Modelled on the quick-switchers users
// already know (Notion's own search, GitHub's repo switcher) rather than a bespoke pattern:
// type to narrow, arrows to move, enter to commit. Everything here is sized for a 340×400 card
// floating on the canvas, so density and keyboard reach matter more than ornament.

type Props = {
  provider: ConnectorProvider
  /** Targets already on this board, so the same repo/page can't be added twice. */
  existingTargets: string[]
  onCommit: (value: string) => void
  onCancel: () => void
}

type Option = {
  target: string
  label: string
  /** Rendered ahead of the label, de-emphasized (a repo's owner). */
  prefix?: string
  /** Notion emoji, when the page has one. */
  emoji?: string | null
  isDatabase?: boolean
  /** The Google account's own calendar, listed last and tagged as personal. */
  isPrimary?: boolean
  isCalendar?: boolean
}

const PROVIDER_NAME: Record<ConnectorProvider, string> = {
  github: 'GitHub',
  notion: 'Notion',
  google_calendar: 'Google Calendar',
}

// Notion ids are 32 hex chars, with or without dashes, and links end in one.
const NOTION_ID_RE = /([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

function extractNotionId(input: string): string | null {
  return input.trim().match(NOTION_ID_RE)?.[1]?.replace(/-/g, '') ?? null
}

// Last resort label when a pasted link carries no title and the API can't resolve one: Notion
// slugs read "Some-Page-Title-<id>", so strip the id and un-kebab what's left.
function labelFromNotionUrl(input: string): string {
  const slug = input.trim().split('/').pop() ?? ''
  const title = slug
    .split('?')[0]!
    .replace(/-?[0-9a-fA-F]{32}$/, '')
    .replace(/-/g, ' ')
    .trim()
  return title || 'Notion'
}

function useDebounced(value: string, ms: number) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export function ConnectorPicker({ provider, existingTargets, onCommit, onCancel }: Props) {
  const isGithub = provider === 'github'
  const isNotion = provider === 'notion'
  const isCalendar = provider === 'google_calendar'
  const utils = trpc.useUtils()
  const [query, setQuery] = useState('')
  const [activeIndexRaw, setActiveIndex] = useState(0)
  const [isResolving, setIsResolving] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Live connection view. The stored repository list is a connect-time snapshot, so the picker
  // reads the same refreshed source the canvas health badges use.
  const { data: status, isLoading: statusLoading } = trpc.brandConnection.connectorStatus.useQuery(
    undefined,
    { staleTime: 60_000 },
  )

  // Notion search runs server-side: the workspace can hold far more than one page of results,
  // and filtering a fixed first page client-side made real pages look like they didn't exist.
  const debouncedQuery = useDebounced(query, 200)
  const { data: notionPages, isFetching: notionFetching } =
    trpc.brandConnection.notionPages.useQuery(
      { query: debouncedQuery.trim() || undefined },
      { enabled: isNotion && !!status?.notion, placeholderData: (prev) => prev },
    )

  // A brand has a handful of calendars at most, so one fetch filtered client-side is enough.
  const {
    data: calendars,
    isLoading: calendarsLoading,
    error: calendarsError,
  } = trpc.brandConnection.listGoogleCalendars.useQuery(undefined, {
    enabled: isCalendar && !!status?.googleCalendar,
    staleTime: 60_000,
    retry: false,
  })

  const connected = isGithub
    ? !!status?.github
    : isNotion
      ? !!status?.notion
      : !!status?.googleCalendar
  const accountLabel = isGithub
    ? (status?.github?.accountLogin ?? null)
    : isNotion
      ? (status?.notion?.workspaceName ?? null)
      : (status?.googleCalendar?.accountEmail ?? null)

  const existing = useMemo(() => new Set(existingTargets), [existingTargets])

  const options: Option[] = useMemo(() => {
    if (isGithub) {
      const repos = status?.github?.repositories ?? []
      const q = query.trim().toLowerCase()
      return repos
        .filter((r) => !q || `${r.owner}/${r.name}`.toLowerCase().includes(q))
        .map((r) => ({ target: `${r.owner}/${r.name}`, label: r.name, prefix: `${r.owner}/` }))
    }
    if (isCalendar) {
      const q = query.trim().toLowerCase()
      return (calendars ?? [])
        .filter((c) => !q || c.summary.toLowerCase().includes(q))
        .map((c) => ({ target: c.id, label: c.summary, isPrimary: c.primary, isCalendar: true }))
    }
    return (notionPages ?? []).map((p) => ({
      target: p.id,
      label: p.title,
      emoji: p.icon,
      isDatabase: p.type === 'database',
    }))
  }, [isGithub, isCalendar, status?.github?.repositories, calendars, notionPages, query])

  // A pasted link is offered as the first result the moment it's recognisable. Notion's search
  // can't see nested or inline databases at all, so pasting is a first-class path here, not a
  // fallback tucked under the list.
  const pastedId = isNotion ? extractNotionId(query) : null
  const showPasted = !!pastedId && !options.some((o) => o.target === pastedId)

  const rows: Option[] = useMemo(
    () =>
      showPasted && pastedId
        ? [{ target: pastedId, label: labelFromNotionUrl(query) }, ...options]
        : options,
    [showPasted, pastedId, query, options],
  )

  // Clamped at render rather than reset in an effect: results arrive asynchronously and can
  // shrink the list under a selection that was valid a moment ago.
  const activeIndex = Math.min(activeIndexRaw, Math.max(0, rows.length - 1))

  useEffect(() => {
    const raf = requestAnimationFrame(() => searchRef.current?.focus())
    return () => cancelAnimationFrame(raf)
  }, [])

  // The canvas pans/zooms on a native bubble-phase wheel listener that preventDefaults, and
  // React's onWheel runs too late to stop it. Intercept natively so the list scrolls.
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const stop = (e: WheelEvent) => e.stopPropagation()
    el.addEventListener('wheel', stop, { passive: true })
    return () => el.removeEventListener('wheel', stop)
  }, [rows.length])

  // Keep the keyboard-selected row in view.
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${activeIndex}"]`)?.scrollIntoView({
      block: 'nearest',
    })
  }, [activeIndex])

  const commit = async (option: Option) => {
    if (existing.has(option.target)) return
    // A pasted id carries no real title until Notion is asked, and the id alone is unreadable
    // on the canvas forever after.
    if (showPasted && option.target === pastedId) {
      setIsResolving(true)
      const resolved = await utils.brandConnection.notionResolve
        .fetch({ id: option.target })
        .catch(() => null)
      setIsResolving(false)
      onCommit(JSON.stringify({ target: option.target, label: resolved?.title || option.label }))
      return
    }
    onCommit(JSON.stringify({ target: option.target, label: option.label }))
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation()
    if (e.key === 'Escape') return onCancel()
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, rows.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const option = rows[activeIndex]
      if (option) void commit(option)
    }
  }

  const isLoading =
    statusLoading ||
    (isNotion && notionFetching && !notionPages) ||
    (isCalendar && calendarsLoading)

  return (
    <div className="ring-primary ring-offset-background bg-card text-foreground relative flex h-full w-full flex-col overflow-hidden rounded-lg shadow-lg ring-2 ring-offset-2">
      {/* Which account this is drawing from. Invisible before, which is exactly what makes a
          switched connection confusing later. */}
      <header className="flex items-center gap-2 px-3 pb-2 pt-3">
        {isGithub ? (
          <Github size={14} className="text-foreground shrink-0" />
        ) : isCalendar ? (
          <CalendarDays size={14} className="text-foreground shrink-0" />
        ) : (
          <FileText size={14} className="text-foreground shrink-0" />
        )}
        <p className="text-foreground text-sm font-medium">{PROVIDER_NAME[provider]}</p>
        {accountLabel && (
          <p className="text-muted-foreground ml-auto truncate text-xs">{accountLabel}</p>
        )}
      </header>

      {!connected && !statusLoading ? (
        <div className="px-3 pb-3">
          <p className="text-muted-foreground text-xs leading-snug">
            No {PROVIDER_NAME[provider]} connection yet.{' '}
            <Link to="/settings/connections" className="text-primary underline">
              Connect it
            </Link>{' '}
            in settings first.
          </p>
        </div>
      ) : (
        <>
          <div className="relative px-3">
            <Search
              size={13}
              className="text-muted-foreground pointer-events-none absolute left-5 top-1/2 -translate-y-1/2"
            />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActiveIndex(0)
              }}
              onKeyDown={onKeyDown}
              placeholder={
                isGithub
                  ? `Search ${status?.github?.repositories.length ?? 0} repositories`
                  : isCalendar
                    ? 'Search calendars'
                    : 'Search pages, or paste a link'
              }
              className="border-input bg-background focus-visible:border-ring focus-visible:ring-ring/50 text-foreground placeholder:text-muted-foreground w-full rounded-md border py-1.5 pl-7 pr-7 text-sm outline-none focus-visible:ring-[3px]"
            />
            {isNotion && notionFetching && (
              <Loader2
                size={13}
                className="text-muted-foreground pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 animate-spin"
              />
            )}
          </div>

          {isCalendar && !calendarsError && (
            <p className="text-muted-foreground px-3 pt-2 text-xs leading-snug">
              Pick a calendar for public moments like launches, talks and events. CASSI reads it
              every week, and your main calendar usually holds personal plans.
            </p>
          )}

          <div ref={listRef} className="mt-2 min-h-0 flex-1 overflow-y-auto px-2 pb-1">
            {isLoading ? (
              <div className="flex flex-col gap-1 px-1">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="bg-muted h-8 animate-pulse rounded-md" />
                ))}
              </div>
            ) : isCalendar && calendarsError ? (
              <p className="text-muted-foreground px-2 py-3 text-xs leading-snug">
                {calendarsError.data?.code === 'PRECONDITION_FAILED'
                  ? 'Google Calendar access expired or was revoked. '
                  : 'Could not load calendars. '}
                <Link to="/settings/connections" className="text-primary underline">
                  Reconnect it
                </Link>{' '}
                in settings.
              </p>
            ) : rows.length === 0 ? (
              <EmptyState provider={provider} hasQuery={!!query.trim()} />
            ) : (
              rows.map((o, i) => {
                const isAdded = existing.has(o.target)
                const isPastedRow = showPasted && i === 0
                return (
                  <button
                    key={o.target}
                    type="button"
                    data-index={i}
                    disabled={isAdded}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => void commit(o)}
                    className={[
                      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                      i === activeIndex && !isAdded ? 'bg-hover' : '',
                      isAdded ? 'cursor-default opacity-50' : '',
                    ].join(' ')}>
                    <RowIcon option={o} isPastedRow={isPastedRow} />
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {o.prefix && <span className="text-muted-foreground">{o.prefix}</span>}
                      <span className="text-foreground font-medium">{o.label}</span>
                    </span>
                    {isPastedRow && !isAdded && (
                      <span className="text-muted-foreground shrink-0 text-[10px] uppercase tracking-wide">
                        {isResolving ? 'Adding…' : 'Paste'}
                      </span>
                    )}
                    {o.isPrimary && (
                      <span className="text-muted-foreground shrink-0 text-[10px] uppercase tracking-wide">
                        Main, personal
                      </span>
                    )}
                    {o.isDatabase && !isPastedRow && (
                      <span className="text-muted-foreground shrink-0 text-[10px] uppercase tracking-wide">
                        DB
                      </span>
                    )}
                    {isAdded && <Check size={13} className="text-muted-foreground shrink-0" />}
                  </button>
                )
              })
            )}
          </div>

          <footer className="border-border/60 flex items-center justify-between gap-2 border-t px-3 py-2">
            <p className="text-muted-foreground text-[10px] leading-snug">
              {isNotion ? (
                <>Nested databases don&apos;t appear in search — paste their link</>
              ) : isCalendar ? (
                <>Upcoming events only. Attendees are never read.</>
              ) : (
                <>↑↓ to move · ↵ to add</>
              )}
            </p>
            <button
              type="button"
              onClick={onCancel}
              className="text-muted-foreground hover:text-foreground shrink-0 text-[11px] transition-colors">
              Cancel
            </button>
          </footer>
        </>
      )}
    </div>
  )
}

function RowIcon({ option, isPastedRow }: { option: Option; isPastedRow: boolean }) {
  if (isPastedRow) return <Link2 size={13} className="text-muted-foreground shrink-0" />
  if (option.emoji)
    return (
      <span className="w-[13px] shrink-0 text-center text-[13px] leading-none">{option.emoji}</span>
    )
  if (option.isDatabase) return <Database size={13} className="text-muted-foreground shrink-0" />
  if (option.isCalendar)
    return <CalendarDays size={13} className="text-muted-foreground shrink-0" />
  if (option.prefix !== undefined)
    return <Github size={13} className="text-muted-foreground shrink-0" />
  return <FileText size={13} className="text-muted-foreground shrink-0" />
}

function EmptyState({ provider, hasQuery }: { provider: ConnectorProvider; hasQuery: boolean }) {
  if (provider === 'github')
    return (
      <p className="text-muted-foreground px-2 py-3 text-xs leading-snug">
        {hasQuery
          ? 'No matching repositories.'
          : 'This connection can’t read any repositories yet. Add some under “Manage repositories” in settings.'}
      </p>
    )
  if (provider === 'google_calendar')
    return (
      <p className="text-muted-foreground px-2 py-3 text-xs leading-snug">
        {hasQuery
          ? 'No matching calendars.'
          : 'This Google account has no calendars CASSI can read events on.'}
      </p>
    )
  return (
    <p className="text-muted-foreground px-2 py-3 text-xs leading-snug">
      {hasQuery
        ? 'Nothing found. If it’s a nested or inline database, paste its link instead — search can’t see those.'
        : 'Nothing shared with the integration yet. Share a page with it in Notion, or paste a link.'}
    </p>
  )
}
